import type { TileStackModel } from '../models/board/TileStackModel';
import type { BoardModel } from '../models/board/BoardModel';

export interface StackConnectivity {
  current: number;
  max: number;
}

export interface BoardConnectivity {
  current: number;
  max: number;
}

function getStackConnectivity(stack: TileStackModel): StackConnectivity {
  const connectedStacks = stack.getConnectedStacks();
  return {
    current: stack.blocker ? 0 : connectedStacks.filter((s) => !s.hasTiles() && !s.blocker).length,
    max: connectedStacks.length,
  };
}

/**
 * Get total connectivity scores for a board. Contains 'max' connectivity counting only connected
 * spaces and 'current' accounting for occupied stacks.
 * @param boardModel
 */
export function getBoardConnectivity(boardModel: BoardModel): BoardConnectivity {
  const stacks = boardModel.getStacks();
  const boardConnectivity: BoardConnectivity = {
    current: 0,
    max: 0,
  };
  stacks.forEach((stack) => {
    const stackConnectivity = getStackConnectivity(stack);
    boardConnectivity.current += stackConnectivity.current;
    boardConnectivity.max += stackConnectivity.max;
  });
  return boardConnectivity;
}

export function getProperConnectivity(boardModel: BoardModel): BoardConnectivity {
  const stacks = boardModel.getStacks();
  const boardConnectivity: BoardConnectivity = {
    current: 0,
    max: 0,
  };
  const excludedStacks = new Set<string>();
  const maxExcludedStacks = new Set<string>();

  stacks.forEach((stack) => {
    const connections = stack.getConnectedStacks();
    const validToCount = !stack.blocker && !stack.hasTiles();

    boardConnectivity.current += validToCount
      ? connections.filter((s) => !s.blocker && !excludedStacks.has(s.id)).length
      : 0;

    if (validToCount) excludedStacks.add(stack.id);
    maxExcludedStacks.add(stack.id);
    boardConnectivity.max += connections.filter((s) => !maxExcludedStacks.has(s.id)).length;
  });

  return boardConnectivity;
}
